import styled from "@emotion/styled";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { LoadingButton } from "@mui/lab";
import {
	Button,
	ButtonGroup,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControlLabel,
	Grid,
	IconButton,
	TextField,
	Tooltip,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { getExpertsDirectStatus } from "@src/api";
import { applyOpenMatch, OpenMatchRequestData, saveQuestionAnswersDraft } from "@src/api/match";
import useUser from "@src/hook/useUser";
import { GroupA } from "@src/ruix";
import { globalSnackbarState } from "@src/state";
import { formatKrwAsManwonInput } from "@src/utils/budget";
import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSetRecoilState } from "recoil";
import { Wrapper } from "../apply/ApplyContent";
import { LightLine, WrapperContainer } from "../base/BasicFormWrapper";
import ProfileListItem from "../profile/components/ProfileListItem";
import { EmptySpace } from "../SignComponent";

// 전문가가 받을 수 있는 금액 계산 함수
const calculateExpertPrice = (expectedPrice: string) => {
	// "2,000만원" 같은 패턴에서 금액 부분만(콤마 포함) 추출
	const m = expectedPrice.match(/([\d,]+)\s*만원/);
	if (!m) return expectedPrice;

	const priceInManwon = Number(m[1].replace(/,/g, "")); // "2,000" -> 2000
	const expertPrice = Math.ceil(priceInManwon * 0.75);

	return `${expertPrice.toLocaleString()}만원`;
};

// 빈 값을 표시하는 함수
const getDisplayText = (value: string | undefined | null, defaultText: string = "") => {
	const hasValue = value && value.trim() !== "";
	return hasValue ? value : defaultText;
};

const OpenWorkRequest = (props: OpenMatchRequestData) => {
	// const { list } = props;

	const {
		matchUuid,
		job,
		endTime,
		client,
		time,
		expectedPrice,
		clientBudget,
		comment,
		neededSkills,
		workType,
		workPurpose,
		workDetail,
		brandName,
		brandUrl,
		businessType,
		type,
		isMe,
		remoteWork,
	} = props;

	const queryClient = useQueryClient();
	const [openAcceptD, setOpenAcceptD] = useState(false);
	const [openAcceptDescD, setOpenAcceptDescD] = useState(false);
	const [openFeeInfoD, setOpenFeeInfoD] = useState(false);

	const [commentMessage, setCommentMessage] = useState("");
	const [questionAnswers, setQuestionAnswers] = useState<{ question: string; answer: string }[]>([]);
	const [isInitialLoad, setIsInitialLoad] = useState(true);

	const [isAgree, setIsAgree] = useState(false);

	const { user } = useUser();

	const { data: expertStatus, refetch } = useQuery(
		"getExpertsDirectStatus",
		getExpertsDirectStatus,
		{
			enabled: user.isLogin && user.userType === 2,
		},
	);

	const setSnackbareState = useSetRecoilState(globalSnackbarState);
	const { mutate, isLoading, isError } = useMutation(applyOpenMatch, {
		onSuccess: () => {
			setOpenAcceptD(true);
			setOpenAcceptDescD(false);
			queryClient.refetchQueries(["getMatchs"]);
			queryClient.refetchQueries(["getMatchList"]);
		},
		onError: () => {
			setSnackbareState("죄송합니다. 채널톡으로 문의 부탁 드립니다.");
		},
	});

	// 임시저장 mutation
	const { mutate: saveDraftMutate } = useMutation(saveQuestionAnswersDraft, {
		onError: () => {
			// 임시저장 실패는 조용히 처리 (사용자에게 알리지 않음)
		},
	});

	const expertPrice = calculateExpertPrice(expectedPrice);

	// 주 활용 시간 표시 텍스트 계산 (순수 숫자만 "주 X시간" 형식, 한글/영문 포함 시 원본 그대로)
	const rawTime = (time || "").trim();
	const timeDisplayText = 
		rawTime === "" || rawTime === "0"
			? "추후협의"
			: /^\d+$/.test(rawTime)
			? `주 ${rawTime}시간`
			: rawTime;

	// 질문이 있는 경우 초기화 및 DB에서 저장된 답변 불러오기
	useEffect(() => {
		if (props.expertQuestions && Array.isArray(props.expertQuestions) && props.expertQuestions.length > 0) {
			setIsInitialLoad(true);
			// DB에서 저장된 답변 불러오기
			const savedAnswers = props.savedQuestionAnswers;
			if (Array.isArray(savedAnswers) && savedAnswers.length > 0) {
				// 질문이 변경되었을 수 있으므로 현재 질문과 매칭
				const matchedAnswers = props.expertQuestions.map((question) => {
					const saved = savedAnswers.find((a: any) => a && a.question === question);
					return {
						question,
						answer: (saved && typeof saved.answer === 'string') ? saved.answer : "",
					};
				});
				setQuestionAnswers(matchedAnswers);
			} else {
				setQuestionAnswers(
					props.expertQuestions.map((question) => ({
						question,
						answer: "",
					}))
				);
			}
			// 초기 로드 완료 후 약간의 지연을 두고 isInitialLoad를 false로 설정
			setTimeout(() => {
				setIsInitialLoad(false);
			}, 500);
		}
	}, [props.expertQuestions, props.savedQuestionAnswers]);

	// 답변 변경 시 debounce로 DB에 임시저장
	useEffect(() => {
		// 초기 로드 중이거나 질문이 없으면 저장하지 않음
		if (isInitialLoad || questionAnswers.length === 0 || !props.expertQuestions || props.expertQuestions.length === 0) {
			return;
		}

		// 이미 지원한 경우(expertComment가 있으면 이미 지원한 것으로 간주) 임시저장하지 않음
		if (props.expertComment) {
			return;
		}

		// debounce: 1초 후 저장
		const timeoutId = setTimeout(() => {
			// 모든 답변이 비어있지 않은 경우에만 저장
			const hasAnyAnswer = questionAnswers.some((qa) => qa.answer && qa.answer.trim().length > 0);
			if (hasAnyAnswer) {
				saveDraftMutate({
					matchUuid: matchUuid,
					questionAnswers: questionAnswers,
				});
			}
		}, 1000);

		return () => clearTimeout(timeoutId);
	}, [questionAnswers, matchUuid, props.expertQuestions, isInitialLoad, saveDraftMutate, props.expertComment]);

	// 조건부 렌더링을 위한 변수들
	const hasQuestions = Array.isArray(props.expertQuestions) && props.expertQuestions.length > 0;
	const hasSubmittedAnswers = Array.isArray(props.questionAnswers) && props.questionAnswers.length > 0;
	const hasExpertComment = !!props.expertComment;
	const shouldShowReadOnlyAnswers = hasQuestions && user.userType === 2 && hasSubmittedAnswers && hasExpertComment;
	const canWriteAnswers = hasQuestions && user.userType === 2 && type === 2 && !isMe && !hasExpertComment;

	return (
		<Wrapper>
			<WrapperContainer>
				<EmptySpace space={15} />
				<div
					style={{
						fontSize: "32px",
						fontWeight: "bold",
						color: "var(--primary)",
					}}
				>
					{client}
				</div>
				<div
					style={{
						fontSize: "24px",
						fontWeight: 500,
						color: "var(--card-bg)",
					}}
				>
					{job}
				</div>
				<EmptySpace space={15} />
				<GroupA
					style={{
						paddingLeft: "5px",
					}}
					order="row-center-start"
					gap={30}
					fullWidth
				>
					<div
						style={{
							fontSize: "20px",
							fontWeight: "500",
						}}
					>
						매칭 조건
					</div>
					<LightLine
						style={{
							width: "auto",
							flex: "1",
						}}
					/>
				</GroupA>
				<EmptySpace space={15} />
				<ProfileListItem
					dark
					text={getDisplayText(remoteWork)}
					notChip
					title={"비대면 가능 여부"}
				/>
				<ProfileListItem
					dark
					text={timeDisplayText}
					notChip
					title={"주 활용 시간"}
				/>
				<ProfileListItem
					dark
					text={
						clientBudget === 0 || expectedPrice === "0"
							? "추후협의"
							: clientBudget == null
							? undefined
							: expectedPrice
					}
					notChip
					title={user.userType === 2 ? "월 인건비 예산" : "월 인건비 예산\n(VAT 별도)"}
				/>
				{props.monthlyAdBudget !== null && props.monthlyAdBudget !== undefined && (
					<ProfileListItem
						dark
						text={`${formatKrwAsManwonInput(props.monthlyAdBudget)}만원`}
						notChip
						title={"월 평균 광고비 예산"}
					/>
				)}
				{typeof props.canProduceCreativeInternally === "string" &&
					props.canProduceCreativeInternally.trim() !== "" && (
						<ProfileListItem
							dark
							text={
								props.canProduceCreativeInternally === "yes"
									? "예"
									: props.canProduceCreativeInternally === "no"
									? "아니오"
									: "미해당/미정"
							}
							notChip
							title={"내부 소재 제작 가능 여부"}
						/>
					)}
				{user.userType === 2 && (
					<ProfileListItem
						dark
						text={expertPrice}
						invisibleOption
						notChip
						title={"전문가 대금"}
					/>
				)}
				<EmptySpace space={15} />
				<GroupA
					style={{
						paddingLeft: "5px",
					}}
					order="row-center-start"
					gap={30}
					fullWidth
				>
					<div
						style={{
							fontSize: "20px",
							fontWeight: "500",
						}}
					>
						기업 정보
					</div>
					<LightLine
						style={{
							width: "auto",
							flex: "1",
						}}
					/>
				</GroupA>
				<EmptySpace space={15} />
				<ProfileListItem
					dark
					text={getDisplayText(brandName)}
					notChip
					title={"브랜드/서비스"}
				/>
				<ProfileListItem
					dark
					text={getDisplayText(brandUrl)}
					notChip
					link
					title={"홈페이지"}
				/>

				<ProfileListItem dark text={getDisplayText(businessType)} notChip title={"비즈니스 유형"} />
				<EmptySpace space={15} />
				<GroupA
					style={{
						paddingLeft: "5px",
					}}
					order="row-center-start"
					gap={30}
					fullWidth
				>
					<div
						style={{
							fontSize: "20px",
							fontWeight: "500",
						}}
					>
						업무
					</div>
					<LightLine
						style={{
							width: "auto",
							flex: "1",
						}}
					/>
				</GroupA>
				<EmptySpace space={15} />
				{/* 업무 목표 */}
				<ProfileListItem dark text={getDisplayText(workPurpose)} notChip title={"업무 목표"} />
				{/* 현재 상황 */}
				<ProfileListItem dark text={getDisplayText((props as any).currentSituation)} notChip title={"현재 상황"} topAlign />
				{/* 업무 범위 */}
				<ProfileListItem dark text={getDisplayText((props as any).expectedWorkload)} notChip title={"업무 범위"} topAlign />
				{/* 필수 역량 */}
				<ProfileListItem dark text={getDisplayText(neededSkills)} notChip title={"필수 역량"} />
				{/* 필수 역량 helperText */}
				{user.userType !== 2 && (
					<div style={{ 
						marginLeft: '5px', 
						marginTop: '4px', 
						marginBottom: '12px',
						color: '#000', 
						fontSize: '13px', 
						lineHeight: 1.6
					}}>
						필수 역량 조건이 너무 많을 경우, 추천 가능한 전문가의 범위가 과도하게 줄어듭니다. <strong style={{ color: '#1976d2' }}>매칭 정확성 극대화</strong>를 위해, <strong style={{ color: '#1976d2' }}>반드시 지켜져야 하는 조건 외에는</strong> 우대 역량에 작성하는 것을 권장드립니다.
					</div>
				)}
				{/* 우대 역량 (데이터 미정) */}
				<ProfileListItem dark text={getDisplayText((props as any).preferredSkills)} notChip title={"우대 역량"} />
				{/* 내부 협업 가능 인력 (데이터 미정) */}
				<ProfileListItem dark text={getDisplayText((props as any).internalCollaborators)} notChip title={"내부 협업 가능 인력"} />
				{/* 기타 고려사항 */}
				<ProfileListItem dark text={getDisplayText(comment)} notChip title={"기타 고려사항"} topAlign />
				
				{/* 전문가 지원 질문 섹션 - 고객사가 제출한 질문 표시 (고객사 확인용) */}
				{user.userType === 1 && props.expertQuestions && Array.isArray(props.expertQuestions) && props.expertQuestions.length > 0 && (
					<>
						<EmptySpace space={30} />
						<GroupA
							style={{
								paddingLeft: "5px",
							}}
							order="row-center-start"
							gap={30}
							fullWidth
						>
							<div
								style={{
									fontSize: "20px",
									fontWeight: "500",
								}}
							>
								전문가 지원 질문
							</div>
							<LightLine
								style={{
									width: "auto",
									flex: "1",
								}}
							/>
						</GroupA>
						<EmptySpace space={15} />
						{props.expertQuestions.map((question, index) => {
							if (!question || typeof question !== 'string') return null;
							return (
								<div key={index} style={{ marginBottom: "20px" }}>
									<div
										style={{
											fontSize: "16px",
											fontWeight: "600",
											color: "#2c3e50",
											marginBottom: "8px",
										}}
									>
										질문 {index + 1}
									</div>
									<div
										style={{
											backgroundColor: "#f5f5f5",
											padding: "16px",
											borderRadius: "8px",
											border: "1px solid #e0e0e0",
											fontSize: "15px",
											color: "#424242",
											lineHeight: 1.6,
											boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
										}}
									>
										{question}
									</div>
								</div>
							);
						})}
					</>
				)}
				
				{/* 지원 문답 섹션 - 이미 작성된 답변 표시 (읽기 전용) */}
				{shouldShowReadOnlyAnswers && (
					<>
						<EmptySpace space={30} />
						<GroupA
							style={{
								paddingLeft: "5px",
							}}
							order="row-center-start"
							gap={30}
							fullWidth
						>
							<div
								style={{
									fontSize: "20px",
									fontWeight: "500",
								}}
							>
								지원 문답
							</div>
							<LightLine
								style={{
									width: "auto",
									flex: "1",
								}}
							/>
						</GroupA>
						<EmptySpace space={15} />
						{(props.questionAnswers || []).map((qa, index: number) => {
							if (!qa || typeof qa !== 'object') return null;
							const questionText = (qa.question && typeof qa.question === 'string') 
								? qa.question 
								: (props.expertQuestions?.[index] && typeof props.expertQuestions[index] === 'string')
									? props.expertQuestions[index]
									: `질문 ${index + 1}`;
							const answerText = (qa.answer && typeof qa.answer === 'string') ? qa.answer : '';
							return (
								<div key={index} style={{ marginBottom: "24px" }}>
									<div
										style={{
											fontSize: "16px",
											fontWeight: "600",
											color: "#2c3e50",
											marginBottom: "12px",
										}}
									>
										질문 {index + 1}: {questionText}
									</div>
									<div
										style={{
											backgroundColor: "#fafafa",
											padding: "20px",
											borderRadius: "12px",
											border: "1px solid #e8e8e8",
											whiteSpace: "pre-wrap",
											lineHeight: "1.7",
											fontSize: "15px",
											color: "#2c2c2c",
											fontWeight: "400",
											boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
										}}
									>
										{answerText}
									</div>
								</div>
							);
						})}
					</>
				)}

				{/* 지원 문답 작성 섹션 - 새로운 답변 작성 (작성 가능) */}
				{canWriteAnswers && (
					<>
						<EmptySpace space={30} />
						<GroupA
							style={{
								paddingLeft: "5px",
							}}
							order="row-center-start"
							gap={30}
							fullWidth
						>
							<div
								style={{
									fontSize: "20px",
									fontWeight: "500",
								}}
							>
								지원 문답 작성
							</div>
							<LightLine
								style={{
									width: "auto",
									flex: "1",
								}}
							/>
						</GroupA>
						<EmptySpace space={15} />
						{Array.isArray(questionAnswers) && questionAnswers.map((qa, index) => {
							if (!qa || typeof qa !== 'object' || typeof qa.question !== 'string') {
								return null;
							}
							const answerValue = (qa.answer && typeof qa.answer === 'string') ? qa.answer : '';
							return (
								<div key={index} style={{ marginBottom: "24px" }}>
									<div
										style={{
											fontSize: "16px",
											fontWeight: "600",
											color: "#2c3e50",
											marginBottom: "12px",
										}}
									>
										질문 {index + 1}: {qa.question}
									</div>
									<TextField
										fullWidth
										multiline
										minRows={4}
										maxRows={8}
										placeholder="질문에 대한 답변을 작성해주세요. (최대 300자)"
										value={answerValue}
										onChange={(e) => {
											const value = e.target.value;
											// 300자 제한
											if (value.length <= 300) {
												const newAnswers = [...questionAnswers];
												if (newAnswers[index]) {
													newAnswers[index] = { ...newAnswers[index], answer: value };
													setQuestionAnswers(newAnswers);
												}
											}
										}}
										inputProps={{
											maxLength: 300,
										}}
										helperText={`${answerValue.length}/300자`}
										sx={{
											"& .MuiOutlinedInput-root": {
												backgroundColor: "#fafafa",
											},
										}}
									/>
								</div>
							);
						})}
					</>
				)}

				{/* 지원자 코멘트 섹션 - userType이 2이고 expertComment가 존재할 때만 표시 */}
				{user.userType === 2 && props.expertComment && (
					<>
						<div
							style={{
								marginBottom: "24px",
								paddingBottom: "20px",
								// borderTop: "1px solid #f0f0f0",
							}}
						>
							{/* <div
								style={{
									fontSize: "18px",
									fontWeight: "600",
									color: "var(--primary)",
									marginBottom: "16px",
									display: "flex",
									alignItems: "center",
									gap: "8px",
								}}
							>
								지원 코멘트
							</div> */}
							<EmptySpace space={30} />
							<GroupA
								style={{
									paddingLeft: "5px",
								}}
								order="row-center-start"
								gap={30}
								fullWidth
							>
								<div
									style={{
										fontSize: "20px",
										fontWeight: "500",
										marginBottom: "20px",
									}}
								>
								지원 코멘트
								</div>
								<LightLine
									style={{
										width: "auto",
										flex: "1",
									}}
								/>
							</GroupA>
							<div
								style={{
									backgroundColor: "#fafafa",
									padding: "20px",
									borderRadius: "12px",
									border: "1px solid #e8e8e8",
									whiteSpace: "pre-wrap",
									lineHeight: "1.7",
									fontSize: "15px",
									color: "#2c2c2c",
									fontWeight: "400",
									boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
									marginTop: "10px",
								}}
							>
								{props.expertComment}
							</div>
						</div>
					</>
				)}

				{/* 지원 코멘트 작성 섹션 - 전문가가 지원할 때만 표시 */}
				{type === 2 && !isMe && user.userType === 2 && !props.expertComment && (
					<>
						<EmptySpace space={30} />
						<GroupA
							style={{
								paddingLeft: "5px",
							}}
							order="row-center-start"
							gap={30}
							fullWidth
						>
							<div
								style={{
									fontSize: "20px",
									fontWeight: "500",
								}}
							>
								지원 코멘트
							</div>
							<LightLine
								style={{
									width: "auto",
									flex: "1",
								}}
							/>
						</GroupA>
						<EmptySpace space={15} />
						<TextField
							fullWidth
							multiline
							minRows={5}
							maxRows={10}
							placeholder="의사 결정에 참고할 수 있도록 요청 업무와 관련된 경험을 적어주시면 프로필 수락 확률이 높아집니다"
							value={commentMessage}
							onChange={(e) => setCommentMessage(e.target.value)}
							sx={{
								"& .MuiOutlinedInput-root": {
									backgroundColor: "#fafafa",
								},
							}}
						/>
					</>
				)}
				
				<>
					<>
						<EmptySpace space={30} />
						{type === 2 && !isMe && !props.expertComment ? (
							<ButtonGroup fullWidth>
								<LoadingButton
									sx={{
										".MuiLoadingButton-loadingIndicator": {
											color: "white",
										},
									}}
									className="btn"
									fullWidth
									loading={isLoading}
									variant="contained"
									onClick={() => {
										// 모달 열기 전에 검증
										// 1. 지원 코멘트 검증
										if (commentMessage.trim().length <= 0) {
											setSnackbareState("지원 코멘트를 작성해주세요.");
											return;
										}
										// 2. 질문이 있는 경우 모든 답변이 작성되었는지 확인
										if (Array.isArray(props.expertQuestions) && props.expertQuestions.length > 0) {
											// 질문 개수와 답변 개수가 일치하는지 확인
											if (!Array.isArray(questionAnswers) || questionAnswers.length !== props.expertQuestions.length) {
												setSnackbareState("모든 질문에 답변을 작성해주세요.");
												return;
											}
											// 모든 답변이 비어있지 않은지 확인
											const hasEmptyAnswer = questionAnswers.some((qa) => !qa || typeof qa !== 'object' || !qa.answer || typeof qa.answer !== 'string' || qa.answer.trim().length === 0);
											if (hasEmptyAnswer) {
												setSnackbareState("모든 질문에 답변을 작성해주세요.");
												return;
											}
										}
										// 검증 통과 시 모달 열기
										setOpenAcceptDescD(true);
									}}
								>
									지원하기
								</LoadingButton>
							</ButtonGroup>
						) : (
							<></>
						)}
					</>
				</>
				<Dialog
					open={openAcceptDescD}
					onClose={() => {
						setOpenAcceptDescD(false);
					}}
					sx={{
						".MuiDialog-paper": {
							backgroundColor: "var(--bg-white)",
							color: "var(--card-bg)",
							fontWeight: "bold",
							borderRadius: "18px",
							paddingBottom: "10px",
						},
					}}
					fullWidth
					maxWidth={"sm"}
				>
					<DialogTitle
						sx={{
							backgroundColor: "var(--primary)",
							color: "#fff",
							fontWeight: "bold",
						}}
					>
						{"지원하기 전 확인사항"}
					</DialogTitle>
					<EmptySpace space={10} />
					<DialogContent>
						<DialogContentText
							sx={{
								color: "var(--card-bg)",
								fontSize: "14px",
								lineHeight: 1.6,
							}}
						>
							<Typography sx={{ mb: 2, fontSize: "14px" }}>
								프로필의 업무 적합도 및 내부 기준에 따라 일부 지원 전문가에게 원포인트 추천 뱃지가 부여됩니다.
							</Typography>
							<Typography sx={{ mb: 3, fontSize: "14px" }}>
								안전한 거래를 위해 플랫폼 외 소통 및 고객사와의 직접 거래는 금지되어 있으며, 위반 시 원포인트 서비스 계약서 제6조 7항에 따라 활동 제한 또는 손해배상의 책임이 발생할 수 있습니다.
							</Typography>
							<FormControlLabel
								control={
									<Checkbox
										onClick={() => {
											setIsAgree(!isAgree);
										}}
										checked={isAgree}
									/>
								}
								label="위 내용을 확인하였으며, 원포인트 내에서의 안전한 거래에 동의합니다."
								sx={{
									"& .MuiFormControlLabel-label": {
										fontSize: "14px",
									},
								}}
							/>
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => {
								setOpenAcceptDescD(false);
							}}
						>
							취소
						</Button>
						<LoadingButton
							sx={{
								".MuiLoadingButton-loadingIndicator": {
									color: "var(--card-bg)",
								},
							}}
							variant="contained"
							loading={isLoading}
							onClick={() => {
								if (!isAgree) {
									setSnackbareState("위 내용에 동의해주세요.");
									return;
								}
								// 모달에서는 동의 확인만 하고 바로 지원 진행 (검증은 이미 모달 열기 전에 완료됨)
								mutate({
									matchUuid: matchUuid,
									comment: commentMessage,
									questionAnswers: (Array.isArray(props.expertQuestions) && props.expertQuestions.length > 0 && Array.isArray(questionAnswers)) 
										? questionAnswers 
										: undefined,
								});
							}}
						>
							<Typography sx={{ fontWeight: "bold" }}>지원하기</Typography>
						</LoadingButton>
					</DialogActions>
				</Dialog>
				<Dialog
					open={openAcceptD}
					onClose={() => {
						setOpenAcceptD(false);
					}}
					sx={{
						".MuiDialog-paper": {
							backgroundColor: "white",
							color: "var(--card-bg)",
							fontWeight: "bold",
							borderRadius: "18px",
							paddingBottom: "10px",
						},
					}}
					maxWidth={"xs"}
				>
					<DialogTitle
						sx={{
							backgroundColor: "var(--primary)",
							color: "#fff",
							fontWeight: "bold",
						}}
					>
						{"인터뷰 지원이 완료되었습니다"}
					</DialogTitle>
					<EmptySpace space={10} />
					<DialogContent>
						<DialogContentText
							sx={{
								color: "var(--card-bg)",
							}}
						>
							{`고객사 측에 전문가님 프로필을 전달 예정입니다.\n고객사 의사 결정에 도움이 되도록 프로필 보완이\n필요하다면 수정 부탁드립니다.`}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button
							variant="contained"
							onClick={() => {
								//
								setOpenAcceptD(false);
								setCommentMessage("");
								window.open(
									"https://docs.channel.io/onepoint/ko/articles/%EA%B3%A0%EA%B0%9D%EC%82%AC-%EB%A7%A4%EC%B9%AD-%ED%94%84%EB%A1%9C%EC%84%B8%EC%8A%A4-4337bd80",
								);
							}}
							autoFocus
							fullWidth
						>
							<Typography sx={{ fontWeight: "bold" }}>확인</Typography>
						</Button>
					</DialogActions>
					<EmptySpace space={20} />
				</Dialog>
				<EmptySpace space={30} />
				<div
					className="risk-free-text"
					style={{
						display: "flex",
						flexWrap: "wrap",
						alignItems: "center",
						gap: "4px",
					}}
				>
					<span>원포인트의 모든 계약은 Risk-Free 프로그램이 적용됩니다.</span>
					<span style={{ whiteSpace: "nowrap" }}>(프로젝트 유형 제외)</span>
					<a
						href="https://docs.channel.io/onepoint/ko/articles/%EB%A6%AC%EC%8A%A4%ED%81%AC-%ED%94%84%EB%A6%AC-%EC%A0%95%EC%B1%85-%EC%95%88%EB%82%B4-1286c10a"
						target="_blank"
						rel="noopener noreferrer"
						style={{
							color: "var(--primary)",
							textDecoration: "none",
							fontWeight: "600",
							whiteSpace: "nowrap",
						}}
					>
						[자세히 보기]
					</a>
					
				</div>
				<EmptySpace space={50} />

				{/* 수수료 정보 모달 */}
				<Dialog
					open={openFeeInfoD}
					onClose={() => {
						setOpenFeeInfoD(false);
					}}
					sx={{
						".MuiDialog-paper": {
							backgroundColor: "var(--bg-white)",
							color: "var(--card-bg)",
							borderRadius: "16px",
							paddingBottom: "10px",
							minWidth: "300px",
						},
					}}
					maxWidth={"sm"}
				>
					<DialogTitle
						sx={{
							backgroundColor: "var(--primary)",
							color: "#fff",
							fontWeight: "bold",
							textAlign: "center",
							padding: "12px 16px",
						}}
					>
						<div>원포인트 제공 서비스</div>
					</DialogTitle>
					<DialogContent sx={{ padding: "20px", paddingBottom: "12px" }}>
						<div style={{ marginBottom: "0px", marginTop: "12px" }}>
							<ul
								style={{
									fontSize: "13px",
									lineHeight: "1.4",
									margin: "0 auto",
									padding: "0",
									listStyle: "none",
									width: "fit-content",
								}}
							>
								<li style={{ marginBottom: "6px", textAlign: "left" }}>
									• 업무 기회를 지속적으로 제공합니다
								</li>
								<li style={{ marginBottom: "6px", textAlign: "left" }}>
									• 고객 대금 미지급 리스크를 대신 부담합니다
								</li>
								<li style={{ marginBottom: "6px", textAlign: "left" }}>
									• 결제 정산 등 번거로운 업무를 대행합니다
								</li>
								<li style={{ marginBottom: "6px", textAlign: "left" }}>
									• 분쟁 발생시 중재를 도와드립니다
								</li>
							</ul>
						</div>
					</DialogContent>
					<DialogActions sx={{ padding: "0 20px 16px" }}>
						<Button
							variant="contained"
							onClick={() => {
								setOpenFeeInfoD(false);
							}}
							autoFocus
							fullWidth
							sx={{ borderRadius: "8px" }}
						>
							확인
						</Button>
					</DialogActions>
				</Dialog>
			</WrapperContainer>
		</Wrapper>
	);
};
export default OpenWorkRequest;

const TopDataBox = styled.div`
	background-color: var(--bg-white);
	padding: 4px;

	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-around;
	flex-wrap: wrap;

	@media screen and (max-width: 768px) {
		flex-direction: column;
		align-items: start;
		justify-content: start;
	}
	div {
		font-size: 18px;
		font-weight: 600;
		line-height: 30px;
	}
`;

const TopDataEmpty = styled.div`
	height: 15px;

	@media screen and (max-width: 768px) {
		height: auto;
	}
`;
